import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Typography, Chip, TextField, InputAdornment, IconButton,
  FormControlLabel, Switch, Grid, useTheme, Tooltip, Button,
  Dialog, DialogTitle, DialogContent, DialogActions,
  FormControl, InputLabel, Select, MenuItem, CircularProgress,
  Paper, Checkbox,
} from '@mui/material';
import {
  Search as SearchIcon, Refresh as RefreshIcon,
  Add as AddIcon, Block as BlockIcon, LockOpen as UnblockIcon,
  DeleteOutline as DeleteIcon, FileDownload as ExportIcon,
  CheckCircle as CheckIcon,
} from '@mui/icons-material';
import { RootState, IOCEntry } from '../types';
import { fetchAllIOCs, ingestIOC, blockIOC, unblockIOC, deleteIOC } from '../store/slices/iocsSlice';
import { fetchClients } from '../store/slices/clientsSlice';
import { getRelativeTimeString } from '../utils/dateUtils';
import { DataTable, Column } from '../components/common';
import { colorUtils } from '../theme';
import { IOCsService } from '../services/iocsService';
import IOCDetailDrawer from '../components/iocs/IOCDetailDrawer';
import IOCBulkActionBar from '../components/iocs/IOCBulkActionBar';

const POLL_INTERVAL_MS = 15_000;
const IOC_TYPES = ['ip', 'domain', 'url', 'file'] as const;

const sourceChipSx = (source: string, theme: any) => {
  const colors: Record<string, string> = {
    manual: theme.palette.primary.main,
    alert_enrichment: theme.palette.info.main,
    threat_feed: theme.palette.warning.main,
  };
  const color = colors[source] || theme.palette.text.tertiary;
  return {
    height: 20, fontSize: '0.62rem', fontWeight: 700,
    fontFamily: '"JetBrains Mono", monospace',
    backgroundColor: colorUtils.withOpacity(color, 0.12),
    color, border: `1px solid ${colorUtils.withOpacity(color, 0.3)}`,
    '& .MuiChip-label': { px: 0.75 },
  };
};

const IOCsPage: React.FC = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { iocs, isLoading, error } = useSelector((state: RootState) => state.iocs);
  const { clients } = useSelector((state: RootState) => state.clients);

  const [searchTerm, setSearchTerm] = useState('');
  const [maliciousOnly, setMaliciousOnly] = useState(false);
  const [blockedOnly, setBlockedOnly] = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState<Date>(new Date());
  const [clientsReady, setClientsReady] = useState(false);
  const [actioningId, setActioningId] = useState<string | null>(null);

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [detailIOC, setDetailIOC] = useState<IOCEntry | null>(null);

  // Add IOC dialog
  const [addOpen, setAddOpen] = useState(false);
  const [addClientId, setAddClientId] = useState('');
  const [addValue, setAddValue] = useState('');
  const [addType, setAddType] = useState<string>('ip');
  const [addBlock, setAddBlock] = useState(false);
  const [addNotes, setAddNotes] = useState('');
  const [addLoading, setAddLoading] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);

  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (clients.length === 0) dispatch(fetchClients() as any);
    else setClientsReady(true);
  }, []);

  useEffect(() => {
    if (clients.length > 0) {
      setClientsReady(true);
      if (!addClientId) setAddClientId(clients[0].id);
    }
  }, [clients]);

  const load = () => {
    dispatch(fetchAllIOCs({
      malicious_only: maliciousOnly || undefined,
      is_blocked: blockedOnly || undefined,
    } as any) as any);
    setLastRefreshed(new Date());
  };

  useEffect(() => { if (!clientsReady) return; load(); }, [clientsReady, maliciousOnly, blockedOnly]);

  useEffect(() => {
    if (!clientsReady) return;
    if (pollingRef.current) clearInterval(pollingRef.current);
    pollingRef.current = setInterval(load, POLL_INTERVAL_MS);
    return () => { if (pollingRef.current) clearInterval(pollingRef.current); };
  }, [clientsReady, maliciousOnly, blockedOnly]);

  // Clear selection on filter change
  useEffect(() => { setSelectedIds(new Set()); }, [maliciousOnly, blockedOnly, searchTerm]);

  const filteredIOCs = useMemo(() => {
    if (!searchTerm) return iocs;
    const term = searchTerm.toLowerCase();
    return iocs.filter(ioc =>
      ioc.ioc_value.toLowerCase().includes(term) ||
      ioc.ioc_type.toLowerCase().includes(term) ||
      (ioc.country_code ?? '').toLowerCase().includes(term) ||
      (ioc.isp ?? '').toLowerCase().includes(term) ||
      (ioc.usage_type ?? '').toLowerCase().includes(term) ||
      (ioc.notes ?? '').toLowerCase().includes(term) ||
      (ioc.tags ?? []).some(t => t.toLowerCase().includes(term))
    );
  }, [iocs, searchTerm]);

  const allSelected = filteredIOCs.length > 0 && filteredIOCs.every(i => selectedIds.has(i.id));
  const someSelected = filteredIOCs.some(i => selectedIds.has(i.id));

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredIOCs.map(i => i.id)));
    }
  };

  const handleBlock = async (ioc: IOCEntry, e: React.MouseEvent) => {
    e.stopPropagation();
    setActioningId(ioc.id);
    try {
      if (ioc.is_currently_blocked) {
        await (dispatch(unblockIOC(ioc.id) as any)).unwrap();
      } else {
        await (dispatch(blockIOC({ id: ioc.id }) as any)).unwrap();
      }
    } finally { setActioningId(null); }
  };

  const handleDelete = async (ioc: IOCEntry, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!window.confirm(`Delete IOC ${ioc.ioc_value}?`)) return;
    setActioningId(ioc.id);
    try {
      await (dispatch(deleteIOC(ioc.id) as any)).unwrap();
      if (detailIOC?.id === ioc.id) setDetailIOC(null);
    } finally { setActioningId(null); }
  };

  const handleAdd = async () => {
    setAddLoading(true);
    setAddError(null);
    try {
      await (dispatch(ingestIOC({
        client_id: addClientId,
        ioc_value: addValue.trim(),
        ioc_type: addType,
        notes: addNotes.trim() || undefined,
        block: addBlock,
      }) as any)).unwrap();
      setAddOpen(false);
      setAddValue(''); setAddNotes(''); setAddBlock(false);
    } catch (e: any) {
      setAddError(e.message || 'Failed to add IOC');
    } finally { setAddLoading(false); }
  };

  const handleExport = (clientId: string) => {
    const url = IOCsService.getExportUrl(clientId, {
      is_malicious: maliciousOnly || undefined,
      is_blocked: blockedOnly || undefined,
    });
    window.open(url, '_blank');
  };

  const scoreColor = (score: number | null, malicious: boolean | null) => {
    if (malicious) return theme.palette.error.main;
    if (score == null) return theme.palette.text.disabled;
    if (score >= 50) return theme.palette.warning.main;
    return theme.palette.success.main;
  };

  const columns: Column<IOCEntry>[] = [
    {
      key: 'select' as any,
      header: '',
      headerRender: () => (
        <Checkbox
          size="small"
          indeterminate={someSelected && !allSelected}
          checked={allSelected}
          onChange={toggleSelectAll}
          onClick={e => e.stopPropagation()}
          sx={{ p: 0.25 }}
        />
      ),
      minWidth: 44,
      align: 'center',
      render: (ioc) => (
        <Checkbox
          size="small"
          checked={selectedIds.has(ioc.id)}
          onChange={() => {
            setSelectedIds(prev => {
              const next = new Set(prev);
              next.has(ioc.id) ? next.delete(ioc.id) : next.add(ioc.id);
              return next;
            });
          }}
          onClick={e => e.stopPropagation()}
          sx={{ p: 0.25 }}
        />
      ),
    },
    {
      key: 'source',
      header: 'Source',
      minWidth: 110,
      align: 'left',
      render: (ioc) => <Chip label={ioc.source.replace('_', ' ')} size="small" sx={sourceChipSx(ioc.source, theme)} />,
    },
    {
      key: 'ioc_type',
      header: 'Type',
      minWidth: 70,
      align: 'center',
      render: (ioc) => (
        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.78rem', color: theme.palette.text.secondary }}>
          {ioc.ioc_type}
        </Typography>
      ),
    },
    {
      key: 'ioc_value',
      header: 'Value',
      minWidth: 180,
      align: 'left',
      sortable: true,
      sortValue: (ioc) => ioc.ioc_value,
      render: (ioc) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Typography variant="body2" sx={{
            fontFamily: 'monospace', fontSize: '0.82rem',
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 220,
          }}>
            {ioc.ioc_value}
          </Typography>
          {ioc.is_currently_blocked && <BlockIcon sx={{ fontSize: 13, color: theme.palette.error.main, flexShrink: 0 }} />}
        </Box>
      ),
    },
    {
      key: 'reputation_score',
      header: 'Score',
      minWidth: 70,
      align: 'center',
      sortable: true,
      sortValue: (ioc) => ioc.reputation_score ?? -1,
      render: (ioc) => (
        <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.85rem', color: scoreColor(ioc.reputation_score, ioc.is_malicious) }}>
          {ioc.reputation_score ?? '—'}
        </Typography>
      ),
    },
    {
      key: 'is_malicious',
      header: 'Malicious',
      minWidth: 90,
      align: 'center',
      render: (ioc) => (
        <Chip
          label={ioc.is_malicious ? 'Yes' : ioc.is_malicious === null ? '?' : 'No'}
          size="small"
          sx={{
            height: 20, fontSize: '0.68rem', fontWeight: 700,
            backgroundColor: colorUtils.withOpacity(ioc.is_malicious ? theme.palette.error.main : theme.palette.text.disabled, 0.12),
            color: ioc.is_malicious ? theme.palette.error.main : theme.palette.text.disabled,
          }}
        />
      ),
    },
    {
      key: 'country_code',
      header: 'Country',
      minWidth: 80,
      align: 'left',
      render: (ioc) => (
        <Typography variant="body2" sx={{ color: ioc.country_code ? theme.palette.text.primary : theme.palette.text.disabled }}>
          {ioc.country_code ?? '—'}
        </Typography>
      ),
    },
    {
      key: 'isp',
      header: 'ISP',
      minWidth: 140,
      maxWidth: 200,
      align: 'left',
      render: (ioc) => (
        <Typography variant="body2" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: ioc.isp ? theme.palette.text.primary : theme.palette.text.disabled }}>
          {ioc.isp ?? '—'}
        </Typography>
      ),
    },
    {
      key: 'blocked_until',
      header: 'Blocked Until',
      minWidth: 110,
      align: 'left',
      render: (ioc) => {
        if (!ioc.is_blocked) return <Typography variant="body2" sx={{ color: theme.palette.text.disabled }}>—</Typography>;
        if (!ioc.blocked_until) return <Typography variant="body2" sx={{ color: theme.palette.error.main, fontSize: '0.78rem' }}>Permanent</Typography>;
        return (
          <Tooltip title={ioc.blocked_until} arrow>
            <Typography variant="body2" sx={{ color: theme.palette.warning.main, fontSize: '0.78rem' }}>
              {getRelativeTimeString(ioc.blocked_until)}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      key: 'tags',
      header: 'Tags',
      minWidth: 100,
      align: 'left',
      render: (ioc) => (
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
          {(ioc.tags || []).slice(0, 3).map(tag => (
            <Chip key={tag} label={tag} size="small" sx={{ height: 18, fontSize: '0.6rem', fontWeight: 600 }} />
          ))}
        </Box>
      ),
    },
    {
      key: 'last_enriched_at',
      header: 'Enriched',
      minWidth: 100,
      align: 'left',
      render: (ioc) => (
        <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontSize: '0.82rem' }}>
          {ioc.last_enriched_at ? getRelativeTimeString(ioc.last_enriched_at) : '—'}
        </Typography>
      ),
    },
    {
      key: 'actions' as any,
      header: '',
      minWidth: 80,
      align: 'right',
      render: (ioc) => (
        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }} onClick={e => e.stopPropagation()}>
          <Tooltip title={ioc.is_currently_blocked ? 'Unblock' : 'Block'}>
            <IconButton
              size="small"
              disabled={actioningId === ioc.id}
              onClick={(e) => handleBlock(ioc, e)}
              sx={{ color: ioc.is_currently_blocked ? theme.palette.warning.main : theme.palette.text.tertiary, p: 0.5 }}
            >
              {actioningId === ioc.id ? <CircularProgress size={14} /> : ioc.is_currently_blocked ? <UnblockIcon sx={{ fontSize: 16 }} /> : <BlockIcon sx={{ fontSize: 16 }} />}
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              size="small"
              disabled={actioningId === ioc.id}
              onClick={(e) => handleDelete(ioc, e)}
              sx={{ color: theme.palette.text.tertiary, p: 0.5, '&:hover': { color: theme.palette.error.main } }}
            >
              <DeleteIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{
      backgroundColor: theme.palette.background.default,
      minHeight: '100vh', width: '100%',
      display: 'flex', justifyContent: 'center', alignItems: 'flex-start',
      px: { xs: 2, sm: 3, md: 4 }, pt: 10, pb: 3,
    }}>
      <Box sx={{ width: '100%', maxWidth: '100%' }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <Box>
            <Typography variant="h1" sx={{ color: theme.palette.text.primary, fontWeight: 600, mb: 0.5 }}>
              IOC Pool
            </Typography>
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
              Central indicator repository — enriched from alerts, manual ingestion, and threat feeds
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Typography variant="caption" sx={{ color: theme.palette.text.disabled, mr: 1 }}>
              {getRelativeTimeString(lastRefreshed.toISOString())}
            </Typography>
            {clients.length > 0 && (
              <Tooltip title="Export all (CSV)">
                <IconButton onClick={() => handleExport(clients[0].id)} sx={{ bgcolor: theme.palette.background.tertiary, borderRadius: 1, width: 36, height: 36 }}>
                  <ExportIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            )}
            <Button
              variant="contained" size="small"
              startIcon={<AddIcon />}
              onClick={() => setAddOpen(true)}
              sx={{ textTransform: 'none', fontWeight: 600, height: 36 }}
            >
              Add IOC
            </Button>
          </Box>
        </Box>

        {/* Filters */}
        <Paper sx={{ mb: 3, backgroundColor: 'transparent', border: 'none', boxShadow: 'none' }}>
          <Grid container spacing={3} alignItems="flex-end">
            <Grid size={{ xs: 12, md: 5 }}>
              <Typography variant="caption" sx={{ color: theme.palette.text.tertiary, fontWeight: 700, display: 'block', mb: 1, ml: 0.5, letterSpacing: '0.05em' }}>
                Search
              </Typography>
              <TextField
                fullWidth
                placeholder="Search by value, country, ISP, tags, notes…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                size="small"
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: theme.palette.text.tertiary, fontSize: 18 }} /></InputAdornment> }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: theme.palette.background.tertiary, height: 40,
                    '& fieldset': { border: 'none' },
                    '&:hover': { backgroundColor: theme.palette.background.secondary },
                    '&.Mui-focused': { backgroundColor: theme.palette.background.default, boxShadow: `0 0 0 1px ${theme.palette.text.primary}` },
                  },
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 7 }}>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', justifyContent: 'flex-end' }}>
                <FormControlLabel
                  control={<Switch checked={maliciousOnly} onChange={(e) => setMaliciousOnly(e.target.checked)} size="small" color="error" />}
                  label={<Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.85rem' }}>Malicious only</Typography>}
                />
                <FormControlLabel
                  control={<Switch checked={blockedOnly} onChange={(e) => setBlockedOnly(e.target.checked)} size="small" color="warning" />}
                  label={<Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.85rem' }}>Blocked only</Typography>}
                />
                <Tooltip title="Refresh">
                  <IconButton onClick={load} sx={{
                    bgcolor: theme.palette.background.tertiary, color: theme.palette.info.main,
                    borderRadius: 1, width: 40, height: 40,
                    '&:hover': { bgcolor: theme.palette.info.main, color: theme.palette.background.tertiary, transform: 'rotate(180deg)' },
                    transition: 'all 0.4s ease',
                  }}>
                    <RefreshIcon sx={{ fontSize: 20 }} />
                  </IconButton>
                </Tooltip>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Table */}
        <DataTable
          columns={columns}
          data={filteredIOCs}
          rowKey={(ioc) => ioc.id}
          onRowClick={(ioc) => setDetailIOC(ioc)}
          activeRowKey={detailIOC?.id ?? null}
          isLoading={isLoading}
          error={error}
          emptyMessage="No IOCs in pool"
          emptySubMessage={
            maliciousOnly ? 'No malicious IOCs found'
            : blockedOnly ? 'No blocked IOCs'
            : 'IOCs appear automatically after alert enrichment, or add them manually'
          }
          loadingMessage="Loading IOC pool…"
          onRetry={load}
          minTableWidth={700}
          rowHeight={52}
        />
      </Box>

      {/* Detail drawer */}
      <IOCDetailDrawer
        ioc={detailIOC}
        onClose={() => setDetailIOC(null)}
        onUpdated={(updated) => {
          setDetailIOC(updated);
          // Slice already updated the store via updateIOC.fulfilled — just sync local state
        }}
      />

      {/* Bulk action bar */}
      <IOCBulkActionBar
        selectedIds={selectedIds}
        allIocs={filteredIOCs}
        onClearSelection={() => setSelectedIds(new Set())}
        onActionComplete={() => { setSelectedIds(new Set()); load(); }}
      />

      {/* Add IOC Dialog */}
      <Dialog open={addOpen} onClose={() => { setAddOpen(false); setAddError(null); }} maxWidth="sm" fullWidth disableScrollLock
        PaperProps={{ sx: { backgroundColor: theme.palette.background.paper, border: `1px solid ${theme.palette.border.main}`, borderRadius: 2 } }}>
        <DialogTitle sx={{ fontWeight: 700, borderBottom: `1px solid ${theme.palette.border.light}` }}>
          Add IOC to Pool
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Client</InputLabel>
              <Select value={addClientId} label="Client" onChange={(e) => setAddClientId(e.target.value)}>
                {clients.map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
              </Select>
            </FormControl>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth label="IOC Value" size="small"
                placeholder="1.2.3.4 / example.com / sha256…"
                value={addValue} onChange={(e) => setAddValue(e.target.value)}
              />
              <FormControl size="small" sx={{ minWidth: 100 }}>
                <InputLabel>Type</InputLabel>
                <Select value={addType} label="Type" onChange={(e) => setAddType(e.target.value)}>
                  {IOC_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                </Select>
              </FormControl>
            </Box>
            <TextField
              fullWidth label="Notes (optional)" size="small" multiline rows={2}
              value={addNotes} onChange={(e) => setAddNotes(e.target.value)}
            />
            <FormControlLabel
              control={<Switch checked={addBlock} onChange={(e) => setAddBlock(e.target.checked)} color="error" />}
              label={<Typography variant="body2">Block immediately (applies decay TTL per type)</Typography>}
            />
            {addError && <Typography variant="body2" color="error">{addError}</Typography>}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: `1px solid ${theme.palette.border.light}` }}>
          <Button onClick={() => { setAddOpen(false); setAddError(null); }} sx={{ textTransform: 'none', color: theme.palette.text.secondary }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={addLoading || !addValue.trim() || !addClientId}
            onClick={handleAdd}
            startIcon={addLoading ? <CircularProgress size={14} /> : <CheckIcon />}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Add IOC
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default IOCsPage;
